import React, { useEffect, useState, useMemo } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Collapse,
  InputAdornment,
  Chip,
  Menu,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Label';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import api from '../api';
import * as MuiIcons from '@mui/icons-material';
import AheadIcon from './AheadIcon';

// Curated list of 50 common icons suitable for task categories
const ICON_OPTIONS = [
  'Label',              // Default/General
  'Work',               // Work
  'WorkOutline',        // Work alternative
  'School',             // Education
  'Home',               // Home/Personal
  'Star',               // Important/Favorite
  'Favorite',           // Favorite
  'ShoppingCart',       // Shopping
  'ShoppingBag',        // Shopping alternative
  'FitnessCenter',      // Fitness/Health
  'DirectionsRun',      // Running/Exercise
  'Book',              // Reading/Learning
  'Event',              // Events
  'CalendarToday',      // Calendar
  'Pets',               // Pets
  'TravelExplore',      // Travel
  'MusicNote',          // Music
  'LocalDining',        // Dining/Food
  'Restaurant',         // Restaurant
  'Fastfood',           // Fast food
  'LocalCafe',          // Coffee/Cafe
  'HealthAndSafety',    // Health
  'LocalHospital',      // Medical
  'Build',              // Construction/DIY
  'Brush',              // Art/Creative
  'Palette',            // Art/Design
  'CameraAlt',          // Photography
  'Flight',             // Travel/Aviation
  'DirectionsCar',      // Transportation
  'SportsSoccer',       // Sports
  'Weekend',            // Leisure
  'Code',               // Programming
  'Computer',           // Technology
  'AttachMoney',        // Finance
  'AccountBalance',     // Banking
  'Eco',                // Environment
  'Lightbulb',          // Ideas
  'EmojiObjects',       // Ideas alternative
  'Movie',              // Entertainment
  'Games',              // Gaming
  'Spa',                // Wellness
  'ThumbUp',            // Approval/Like
  'Watch',              // Time/Watches
  'Whatshot',           // Trending/Hot
  'LocalLibrary',       // Library/Books
  'LocalOffer',         // Deals/Offers
  'Mood',               // Mood/Emotions
  'Face',               // People
  'Business',           // Business
  'Store',              // Store/Retail
];

const CategorySidebar = React.memo(({ selectedCategory, onSelectCategory, onCategoryAdded, onCategoryUpdated, onCategoryDeleted, collapsed, onToggleCollapse, focusMode, onSelectFocus, searchQuery, onSearchChange, taskCounts }) => {
  const [categories, setCategories] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', icon: 'Label' });
  const [loading, setLoading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropPosition, setDropPosition] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuCategory, setMenuCategory] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Server already sorts by order, but ensure consistency
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (editingCategory) {
        // Update existing category
        const response =         await api.put(
          `/api/categories/${editingCategory._id}`,
          newCategory,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Notify parent component that a category was updated
        if (onCategoryUpdated) {
          onCategoryUpdated(response.data);
        }
      } else {
        // Create new category
        const response = await api.post(
          `/api/categories`,
          newCategory,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Notify parent component that a new category was added
        if (onCategoryAdded) {
          onCategoryAdded(response.data);
        }
      }
      setNewCategory({ name: '', icon: 'Label' });
      setEditingCategory(null);
      setOpenDialog(false);
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setNewCategory({
      name: category.name,
      icon: category.icon
    });
    setOpenDialog(true);
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Notify parent component that a category was deleted
      if (onCategoryDeleted) {
        onCategoryDeleted(id);
      }
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleMenuOpen = (event, category) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuCategory(category);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuCategory(null);
  };

  const handleMenuEdit = () => {
    if (menuCategory) {
      handleEditCategory(menuCategory);
    }
    handleMenuClose();
  };

  const handleMenuDelete = () => {
    if (menuCategory) {
      handleDeleteCategory(menuCategory._id);
    }
    handleMenuClose();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    setNewCategory({ name: '', icon: 'Label' });
  };

  // Drag and drop handlers for category reordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedIndex === null || draggedIndex === index) {
      setDropPosition(null);
      return;
    }
    
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY;
    const elementCenter = rect.top + rect.height / 2;
    
    if (mouseY < elementCenter) {
      setDropPosition({ index, position: 'above' });
    } else {
      setDropPosition({ index, position: 'below' });
    }
  };

  const handleDragLeave = () => {
    setDropPosition(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === undefined) {
      setDraggedIndex(null);
      setDropPosition(null);
      return;
    }
    
    // Don't do anything if dropping on the same position
    if (draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDropPosition(null);
      return;
    }
    
    const newCategories = [...sortedCategories];
    
    // Validate indices
    if (draggedIndex < 0 || draggedIndex >= newCategories.length || 
        dropIndex < 0 || dropIndex >= newCategories.length) {
      console.error('Invalid drag/drop indices');
      setDraggedIndex(null);
      setDropPosition(null);
      return;
    }
    
    const draggedCategory = newCategories[draggedIndex];
    
    // Remove the dragged item
    newCategories.splice(draggedIndex, 1);
    
    // Calculate the new index based on drop position
    let newIndex = dropIndex;
    if (dropPosition && dropPosition.index === dropIndex) {
      if (dropPosition.position === 'above') {
        newIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
      } else {
        newIndex = draggedIndex < dropIndex ? dropIndex : dropIndex + 1;
      }
    } else {
      // Fallback: adjust based on direction
      if (draggedIndex < dropIndex) {
        newIndex = dropIndex;
      } else {
        newIndex = dropIndex + 1;
      }
    }
    
    // Ensure newIndex is within bounds
    newIndex = Math.max(0, Math.min(newIndex, newCategories.length));
    
    // Insert at new position
    newCategories.splice(newIndex, 0, draggedCategory);
    
    // Update local state immediately for better UX
    setCategories(newCategories);
    
    // Update order on server
    try {
      const token = localStorage.getItem('token');
      const categoryIds = newCategories.map(cat => cat._id).filter(id => id); // Filter out any null/undefined
      
      if (categoryIds.length === 0) {
        console.error('No valid category IDs to update');
        fetchCategories(); // Revert
        return;
      }
      
      await api.put(
        '/api/categories/order/update',
        { categoryIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh to get updated data from server
      fetchCategories();
    } catch (error) {
      console.error('Error updating category order:', error);
      // Revert on error
      fetchCategories();
    }
    
    setDraggedIndex(null);
    setDropPosition(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDropPosition(null);
  };

  // Sort categories by order
  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      return new Date(a.createdAt) - new Date(b.createdAt);
    });
  }, [categories]);

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      PaperProps={{ 
        sx: { 
          width: collapsed ? 60 : 240, 
          bgcolor: 'background.paper', 
          borderRight: 'none',
          boxShadow: '2px 0 12px rgba(0, 0, 0, 0.04)',
          transition: 'width 0.3s ease, transform 0.3s ease',
          overflow: 'hidden',
          position: 'fixed',
          height: '100vh',
          zIndex: 1200,
          transform: { xs: collapsed ? 'translateX(-100%)' : 'translateX(0)', sm: 'translateX(0)' },
        } 
      }}
    >
      <Box sx={{ p: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {collapsed ? (
          <AheadIcon size={32} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AheadIcon size={28} />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                ml: 1,
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                letterSpacing: '-0.025em',
                textTransform: 'none',
                fontSize: '1.15em'
              }}
            >
              Ahead
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {!collapsed && (
            <IconButton size="small" onClick={() => setOpenDialog(true)}>
              <AddIcon />
            </IconButton>
          )}
          <IconButton size="small" onClick={onToggleCollapse}>
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>
      </Box>
      
      <Collapse in={!collapsed}>
        <Box sx={{ px: 3, pb: 3 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search tasks..."
            value={searchQuery || ''}
            onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Box>
        <List>
          {/* Focus filter */}
          <ListItem
            button
            selected={focusMode}
            onClick={onSelectFocus}
            sx={{
              borderRadius: '12px',
              mx: 1.5,
              mb: 1.275,
              py: 2,
              transition: 'all 80ms ease-out',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.03)',
                transform: 'translateY(-2px)',
                boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.04)',
              },
              '&.Mui-selected': {
                bgcolor: 'action.selected',
                '&:hover': {
                  bgcolor: 'action.selected',
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, mr: 0.75 }}>
              <StarIcon 
                color={focusMode ? 'warning' : 'disabled'} 
                fontSize="small"
                sx={{ fontSize: '18px' }}
              />
            </ListItemIcon>
            <ListItemText 
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1, position: 'relative', pr: 4 }}>
                  <span style={{ flex: 1, minWidth: 0, lineHeight: 1.5 }}>Focus</span>
                  {taskCounts?.focus?.open > 0 && (
                    <Chip
                      label={taskCounts.focus.open}
                      size="small"
                      sx={{
                        height: 20,
                        minWidth: 20,
                        fontSize: '11px',
                        fontWeight: 500,
                        bgcolor: 'primary.main',
                        color: 'white',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        mr: 5,
                        '& .MuiChip-label': {
                          px: 1,
                          lineHeight: 1,
                        },
                      }}
                    />
                  )}
                </Box>
              }
            />
          </ListItem>
          {/* All and categories */}
          <ListItem
            button
            selected={!focusMode && selectedCategory === null}
            onClick={() => onSelectCategory(null)}
            sx={{
              borderRadius: '12px',
              mx: 1.5,
              mb: 1.275,
              py: 2,
              transition: 'all 80ms ease-out',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.03)',
                transform: 'translateY(-2px)',
                boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.04)',
              },
              '&.Mui-selected': {
                bgcolor: 'action.selected',
                '&:hover': {
                  bgcolor: 'action.selected',
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, mr: 0.75 }}>
              <CategoryIcon fontSize="small" sx={{ fontSize: '18px' }} />
            </ListItemIcon>
            <ListItemText 
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1, position: 'relative', pr: 4 }}>
                  <span style={{ fontWeight: 600, flex: 1, minWidth: 0, lineHeight: 1.5 }}>All</span>
                  {taskCounts?.all?.open > 0 && (
                    <Chip
                      label={taskCounts.all.open}
                      size="small"
                      sx={{
                        height: 20,
                        minWidth: 20,
                        fontSize: '11px',
                        fontWeight: 500,
                        bgcolor: 'primary.main',
                        color: 'white',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        mr: 5,
                        '& .MuiChip-label': {
                          px: 1,
                          lineHeight: 1,
                        },
                      }}
                    />
                  )}
                </Box>
              }
            />
          </ListItem>
          {sortedCategories.map((cat, index) => {
            const Icon = MuiIcons[cat.icon] || CategoryIcon;
            const catId = cat._id.toString();
            const counts = taskCounts?.categories?.[catId] || { open: 0, completed: 0 };
            const isDragging = draggedIndex === index;
            return (
              <ListItem
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                button
                key={cat._id}
                selected={selectedCategory === cat._id}
                onClick={() => onSelectCategory(cat._id)}
                sx={{
                  opacity: isDragging ? 0.3 : 1,
                  cursor: 'grab',
                  borderRadius: '12px',
                  mx: 1.5,
                  mb: 1.275,
                  py: 2,
                  transition: 'all 80ms ease-out',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.03)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.04)',
                  },
                  '&.Mui-selected': {
                    bgcolor: 'action.selected',
                    '&:hover': {
                      bgcolor: 'action.selected',
                    },
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                  '&:hover .category-actions': {
                    opacity: 1,
                    visibility: 'visible',
                    transition: 'opacity 150ms ease-out, visibility 0ms',
                  },
                  position: 'relative',
                  '&::before': dropPosition && dropPosition.index === index && dropPosition.position === 'above' && draggedIndex !== index ? {
                    content: '""',
                    position: 'absolute',
                    top: -2,
                    left: 0,
                    right: 0,
                    height: 2,
                    backgroundColor: 'primary.main',
                    zIndex: 10,
                  } : {},
                  '&::after': dropPosition && dropPosition.index === index && dropPosition.position === 'below' && draggedIndex !== index ? {
                    content: '""',
                    position: 'absolute',
                    bottom: -2,
                    left: 0,
                    right: 0,
                    height: 2,
                    backgroundColor: 'primary.main',
                    zIndex: 10,
                  } : {},
                }}
                secondaryAction={null}
              >
                <ListItemIcon sx={{ minWidth: 36, mr: 0.75 }}>
                  {Icon && <Icon fontSize="small" sx={{ fontSize: '18px' }} />}
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1, position: 'relative', pr: 4 }}>
                      <span style={{ flex: 1, minWidth: 0, lineHeight: 1.5 }}>{cat.name}</span>
                      {counts.open > 0 && (
                        <Chip
                          label={counts.open}
                          size="small"
                          sx={{
                            height: 20,
                            minWidth: 20,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            bgcolor: 'primary.main',
                            color: 'white',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            mr: 5,
                            '& .MuiChip-label': {
                              px: 0.75,
                              lineHeight: 1,
                            },
                          }}
                        />
                      )}
                      <IconButton
                        className="category-actions"
                        size="small"
                        onClick={(e) => handleMenuOpen(e, cat)}
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          opacity: 0,
                          visibility: 'hidden',
                          transition: 'opacity 150ms ease-out, visibility 0ms',
                          padding: '4px',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <MoreVertIcon fontSize="small" sx={{ fontSize: '18px' }} />
                      </IconButton>
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </Collapse>
      
      {collapsed && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
          {/* Focus icon */}
          <IconButton
            size="small"
            selected={focusMode}
            onClick={onSelectFocus}
            sx={{ mb: 1 }}
          >
            <StarIcon color={focusMode ? 'warning' : 'disabled'} sx={{ fontSize: '22.5px' }} />
          </IconButton>
          {/* All icon */}
          <IconButton
            size="small"
            selected={!focusMode && selectedCategory === null}
            onClick={() => onSelectCategory(null)}
            sx={{ mb: 1 }}
          >
            <CategoryIcon sx={{ fontSize: '22.5px' }} />
          </IconButton>
          {sortedCategories.map((cat) => {
            const Icon = MuiIcons[cat.icon] || CategoryIcon;
            return (
              <IconButton
                key={cat._id}
                size="small"
                selected={selectedCategory === cat._id}
                onClick={() => onSelectCategory(cat._id)}
                sx={{ mb: 1 }}
              >
                {Icon && <Icon sx={{ fontSize: '22.5px' }} />}
              </IconButton>
            );
          })}
        </Box>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <form onSubmit={handleAddCategory}>
          <DialogContent>
            <TextField
              fullWidth
              label="Category Name"
              value={newCategory.name}
              onChange={e => setNewCategory({ ...newCategory, name: e.target.value })}
              margin="normal"
              required
            />
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Select Icon
            </Typography>
            <Box sx={{ 
              maxHeight: '400px', 
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1
            }}>
              <Grid container spacing={1}>
                {ICON_OPTIONS.map((iconName) => {
                  const Icon = MuiIcons[iconName] || CategoryIcon;
                  return (
                    <Grid item xs={2} sm={1.5} md={1.2} key={iconName}>
                      <IconButton
                        size="small"
                        color={newCategory.icon === iconName ? 'primary' : 'default'}
                        onClick={() => setNewCategory({ ...newCategory, icon: iconName })}
                        sx={{
                          border: newCategory.icon === iconName ? '2px solid' : '1px solid',
                          borderColor: newCategory.icon === iconName ? 'primary.main' : 'divider',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover'
                          }
                        }}
                        title={iconName}
                      >
                        {Icon && <Icon />}
                      </IconButton>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {editingCategory ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
            mt: 0.5,
            p: 0.5,
          },
        }}
      >
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            onClick={handleMenuEdit}
            size="small"
            sx={{
              color: 'grey.500',
              transition: 'color 0.2s',
              '&:hover': {
                color: 'primary.main',
                bgcolor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <EditIcon sx={{ fontSize: '15px' }} />
          </IconButton>
          <IconButton
            onClick={handleMenuDelete}
            size="small"
            sx={{
              color: 'grey.500',
              transition: 'color 0.2s',
              '&:hover': {
                color: 'error.main',
                bgcolor: 'rgba(244,67,54,0.08)',
              },
            }}
          >
            <DeleteIcon sx={{ fontSize: '15px' }} />
          </IconButton>
        </Box>
      </Menu>
    </Drawer>
  );
});

CategorySidebar.displayName = 'CategorySidebar';

export default CategorySidebar; 